import { AuthPage } from "./routes/AuthPage";
import { BoardRoute } from "./routes/BoardRoute";
import { BoardsRoute } from "./routes/BoardsRoute";
import { FindsRoute } from "./routes/FindsRoute";
import { DiscoveryRoute } from "./routes/DiscoveryRoute";
import { ProductTriageRoute } from "./routes/ProductTriageRoute";
import { ProfileRoute } from "./routes/ProfileRoute";
import { SearchRoute } from "./routes/SearchRoute";
import { BrandLogo } from "./components/BrandLogo";
import { Footer } from "./components/Footer";
import { Button } from "./components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "./components/ui/dropdown-menu";
import { useEffect } from "react";
import { navigate, usePathname } from "./lib/router";
import { clearSession, setSession, useSession, useSessionEmail } from "./lib/session";
import { getCurrentUser, logout } from "./lib/api";

function AvatarMenu({ email }: { email: string | null }) {
  const initial = email ? email[0]!.toUpperCase() : "?";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          id="avatar-btn"
          type="button"
          aria-label="Account menu"
          className="w-9 h-9 rounded-full bg-foreground text-background flex items-center justify-center font-semibold text-sm border-0 cursor-pointer"
        >
          {initial}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onSelect={() => navigate("/profile")}
        >
          Profile
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={async () => {
            await logout();
            clearSession();
            navigate("/", true);
          }}
        >
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function App() {
  const userId = useSession();
  const email = useSessionEmail();
  const pathname = usePathname();

  useEffect(() => {
    if (userId !== null) return;
    getCurrentUser().then((user) => {
      if (user) setSession(user.id, user.email);
    });
  }, []);

  if (userId === null) {
    return <AuthPage />;
  }

  const boardMatch = pathname.match(/^\/boards\/(\d+)$/);
  const boardId = boardMatch ? Number.parseInt(boardMatch[1]!, 10) : null;
  const productMatch = pathname.match(/^\/finds\/(\d+)$/);
  const productId = productMatch ? Number.parseInt(productMatch[1]!, 10) : null;
  const onBoardsRoute = pathname === "/boards";
  const onFindsRoute = pathname === "/finds";
  const onProfileRoute = pathname === "/profile";
  const onSearchRoute = pathname === "/search";
  const searchQuery = onSearchRoute ? new URLSearchParams(window.location.search).get("q") ?? "" : "";

  return (
    <div id="shopping-page" className="active flex flex-col w-full max-w-[1480px] mx-auto min-h-screen">
      <div className="flex items-center justify-between gap-4 p-4 shrink-0">
        <button
          id="brand-home-btn"
          type="button"
          className="brand inline-flex items-center gap-[0.7rem] text-[1.45rem] font-bold tracking-[-0.03em] border-0 bg-transparent p-0"
          onClick={() => navigate("/")}
        >
          <BrandLogo />
          <span>Lustbucket</span>
        </button>
        <div className="flex items-center gap-3">
          <Button
            id="finds-nav-btn"
            variant="ghost"
            className={onFindsRoute ? "text-foreground" : "text-muted"}
            onClick={() => navigate("/finds")}
          >
            Finds
          </Button>
          <Button
            id="boards-nav-btn"
            variant="ghost"
            className={onBoardsRoute ? "text-foreground" : "text-muted"}
            onClick={() => navigate("/boards")}
          >
            Boards
          </Button>
          <AvatarMenu email={email} />
        </div>
      </div>

      <div className="flex-1">
      {productId !== null ? (
        <ProductTriageRoute productId={productId} />
      ) : boardId !== null ? (
        <BoardRoute boardId={boardId} />
      ) : onBoardsRoute ? (
        <BoardsRoute />
      ) : onFindsRoute ? (
        <FindsRoute />
      ) : onProfileRoute ? (
        <ProfileRoute />
      ) : onSearchRoute ? (
        <SearchRoute query={searchQuery} />
      ) : (
        <DiscoveryRoute />
      )}
      </div>
      <Footer />
    </div>
  );
}
